import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { ISalesAccount } from "models/salesAccount/salesAccount.model";
import { fetchImportItemsPrompt } from "services/api/shipments";
import { ImportItemsPromptState } from "services/api/shipments/dtos/import-items-prompt.response.dto";
import { AppThunk, RootState } from "store";
import { ImportItemsPromptSliceInitialState } from "./importItemsPromptSliceInitialState.const";

export const importItemsPromptSlice = createSlice({
  name: "importItemsPromptSlice",
  initialState: ImportItemsPromptSliceInitialState,
  reducers: {
    setImportItemsPromptLoading: (
      state,
      action: PayloadAction<ISalesAccount["uuid"]>
    ) => {
      state.salesAccountUUID = action.payload;
      state.prompt = null;
      state.loadingStatus = ApiLoadingStatus.LOADING;
    },
    setImportItemsPrompt: (
      state,
      action: PayloadAction<{
        salesAccountUUID: ISalesAccount["uuid"];
        prompt: ImportItemsPromptState;
      }>
    ) => {
      state.salesAccountUUID = action.payload.salesAccountUUID;
      state.prompt = action.payload.prompt;
      state.loadingStatus = ApiLoadingStatus.SUCCESS;
    },
    setImportItemsPromptFailed: (
      state,
      action: PayloadAction<ISalesAccount["uuid"]>
    ) => {
      state.salesAccountUUID = action.payload;
      state.prompt = null;
      state.loadingStatus = ApiLoadingStatus.FAILED;
    },
    // The account has just completed its first invoice scan in this session:
    // hide the prompt straight away without waiting for a refetch.
    markImportItemsTried: (state) => {
      state.prompt = {
        eligible: state.prompt?.eligible ?? false,
        hasTried: true,
      };
      state.loadingStatus = ApiLoadingStatus.SUCCESS;
    },
  },
});

export const {
  setImportItemsPromptLoading,
  setImportItemsPrompt,
  setImportItemsPromptFailed,
  markImportItemsTried,
} = importItemsPromptSlice.actions;

// Fetches the verdict once per account. A request that already ran (or is
// running, or failed) for the current account is not repeated, so the form can
// mount this as often as it likes; switching account fetches again.
export const fetchImportItemsPromptThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
      importItemsPromptSlice,
    } = getState() as RootState;

    if (!salesAccountUUID) return;
    if (
      importItemsPromptSlice.salesAccountUUID === salesAccountUUID &&
      importItemsPromptSlice.loadingStatus !== ApiLoadingStatus.IDLE
    ) {
      return;
    }

    dispatch(setImportItemsPromptLoading(salesAccountUUID));

    const { data, error } = await fetchImportItemsPrompt(salesAccountUUID);

    if (!error && data) {
      dispatch(setImportItemsPrompt({ salesAccountUUID, prompt: data }));
    } else {
      dispatch(setImportItemsPromptFailed(salesAccountUUID));
    }
  };
};
